import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Download, Edit, Trash2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `/api`;

const Quotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [formData, setFormData] = useState({
    deal_id: '',
    services: [{ name: '', description: '', amount: '' }],
    payment_terms: '',
    validity_date: '',
    status: 'Draft',
  });

  useEffect(() => {
    fetchQuotes();
    fetchDeals();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/deals`);
      setDeals(response.data.filter(deal => !['Won', 'Lost'].includes(deal.stage)));
    } catch (error) {
      console.error('Failed to load deals');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        services: formData.services.map(s => ({
          ...s,
          amount: parseFloat(s.amount),
        })),
      };
      if (editingQuote) {
        await axios.put(`${API}/quotes/${editingQuote.id}`, payload);
        toast.success('Quote updated successfully');
      } else {
        await axios.post(`${API}/quotes`, payload);
        toast.success('Quote created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quote');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      await axios.delete(`${API}/quotes/${id}`);
      toast.success('Quote deleted successfully');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setFormData({
      ...quote,
      validity_date: quote.validity_date?.split('T')[0] || '',
    });
    setIsDialogOpen(true);
  };

  const handleDownloadPDF = async (quoteId) => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote_${quoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleUpdateStatus = async (quoteId, status) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}`, { status });
      toast.success('Quote status updated');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setEditingQuote(null);
    setFormData({
      deal_id: '',
      services: [{ name: '', description: '', amount: '' }],
      payment_terms: '',
      validity_date: '',
      status: 'Draft',
    });
  };

  const addService = () => {
    setFormData({
      ...formData,
      services: [...formData.services, { name: '', description: '', amount: '' }],
    });
  };

  const removeService = (index) => {
    setFormData({
      ...formData,
      services: formData.services.filter((_, i) => i !== index),
    });
  };

  const updateService = (index, field, value) => {
    const newServices = [...formData.services];
    newServices[index][field] = value;
    setFormData({ ...formData, services: newServices });
  };

  const getTotalAmount = () => {
    return formData.services.reduce((sum, service) => sum + (parseFloat(service.amount) || 0), 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      Draft: 'bg-zinc-800 text-zinc-400 border-zinc-700',
      Sent: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      Accepted: 'bg-green-500/10 text-green-500 border-green-500/20',
      Rejected: 'bg-red-500/10 text-red-500 border-red-500/20',
    };
    return colors[status] || 'bg-zinc-800 text-zinc-400 border-zinc-700';
  };

  const getDealTitle = (dealId) => {
    const deal = deals.find(d => d.id === dealId);
    return deal?.title || 'Unknown Deal';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-quote-button" className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white shadow-[0_0_15px_rgba(59,130,246,0.5)]">
              <Plus className="w-4 h-4 mr-2" />
              Create Quote
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingQuote ? 'Edit Quote' : 'New Quote'}</DialogTitle>
              <DialogDescription className="sr-only">Create or edit quote details and services.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Deal *</Label>
                  <Select value={formData.deal_id} onValueChange={(val) => setFormData({ ...formData, deal_id: val })}>
                    <SelectTrigger data-testid="quote-deal" className="bg-zinc-900/50 border-zinc-800 mt-1">
                      <SelectValue placeholder="Select a deal" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-800">
                      {deals.map(deal => (
                        <SelectItem key={deal.id} value={deal.id}>{deal.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                    <SelectTrigger data-testid="quote-status" className="bg-zinc-900/50 border-zinc-800 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-800">
                      <SelectItem value="Draft">Draft</SelectItem>
                      <SelectItem value="Sent">Sent</SelectItem>
                      <SelectItem value="Accepted">Accepted</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valid Until</Label>
                  <Input
                    data-testid="quote-validity"
                    type="date"
                    value={formData.validity_date}
                    onChange={(e) => setFormData({ ...formData, validity_date: e.target.value })}
                    className="bg-zinc-900/50 border-zinc-800 mt-1"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Services *</Label>
                  <Button type="button" data-testid="add-service-button" onClick={addService} size="sm" className="bg-zinc-800 hover:bg-zinc-700 text-sm">
                    <Plus className="w-3 h-3 mr-1" /> Add Service
                  </Button>
                </div>
                <div className="space-y-3">
                  {formData.services.map((service, index) => (
                    <div key={index} className="bg-app-muted border border-zinc-800 rounded-md p-4">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-sm font-medium text-zinc-300">Service {index + 1}</h4>
                        {formData.services.length > 1 && (
                          <button type="button" data-testid={`remove-service-${index}`} onClick={() => removeService(index)} className="text-red-500 hover:text-red-400">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Input
                            placeholder="Service name"
                            data-testid={`service-name-${index}`}
                            required
                            value={service.name}
                            onChange={(e) => updateService(index, 'name', e.target.value)}
                            className="bg-zinc-900/50 border-zinc-800"
                          />
                        </div>
                        <div>
                          <Input
                            placeholder="Description"
                            data-testid={`service-description-${index}`}
                            required
                            value={service.description}
                            onChange={(e) => updateService(index, 'description', e.target.value)}
                            className="bg-zinc-900/50 border-zinc-800"
                          />
                        </div>
                        <div>
                          <Input
                            placeholder="Amount"
                            data-testid={`service-amount-${index}`}
                            type="number"
                            step="0.01"
                            required
                            value={service.amount}
                            onChange={(e) => updateService(index, 'amount', e.target.value)}
                            className="bg-zinc-900/50 border-zinc-800"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between bg-app-muted border border-zinc-800 rounded-md p-4">
                  <span className="font-semibold text-zinc-100">Total Amount</span>
                  <span className="text-2xl font-bold text-green-500">₹{getTotalAmount().toLocaleString('en-IN')}</span>
                </div>
              </div>

              <div>
                <Label>Payment Terms</Label>
                <Textarea
                  data-testid="quote-payment-terms"
                  value={formData.payment_terms}
                  onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  placeholder="e.g., 50% upfront, 50% on delivery"
                  className="bg-zinc-900/50 border-zinc-800 mt-1"
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" data-testid="cancel-quote-button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="border-zinc-700">
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-quote-button" className="bg-blue-500 hover:bg-blue-600">
                  {editingQuote ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-400">Loading quotes...</div>
      ) : quotes.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">No quotes found</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quotes.map((quote) => (
            <div
              key={quote.id}
              data-testid={`quote-card-${quote.id}`}
              className="bg-app-card border border-app-border rounded-lg p-6 hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-xs text-app-subtext uppercase tracking-wider mb-1">Quote #{quote.id.slice(0, 8)}</p>
                  <h3 className="text-lg font-semibold text-zinc-100">{getDealTitle(quote.deal_id)}</h3>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(quote.status)}`}>
                  {quote.status}
                </span>
              </div>

              <div className="space-y-3 mb-4">
                <div>
                  <p className="text-xs text-app-subtext mb-1">Total Amount</p>
                  <p className="text-2xl font-bold text-green-500">₹{quote.total_amount.toLocaleString('en-IN')}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-app-subtext">Services</p>
                    <p className="text-zinc-100">{quote.services.length}</p>
                  </div>
                  {quote.validity_date && (
                    <div>
                      <p className="text-xs text-app-subtext">Valid Until</p>
                      <p className="text-zinc-100">{new Date(quote.validity_date).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-zinc-800">
                <Button
                  onClick={() => handleDownloadPDF(quote.id)}
                  data-testid={`download-quote-pdf-${quote.id}`}
                  size="sm"
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  PDF
                </Button>
                <button onClick={() => handleEdit(quote)} data-testid={`edit-quote-${quote.id}`} className="p-2 text-blue-500 hover:text-blue-400 transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(quote.id)} data-testid={`delete-quote-${quote.id}`} className="p-2 text-red-500 hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {quote.status === 'Draft' && (
                <Button
                  onClick={() => handleUpdateStatus(quote.id, 'Sent')}
                  data-testid={`mark-sent-button-${quote.id}`}
                  size="sm"
                  variant="outline"
                  className="w-full mt-2 border-zinc-700 text-xs"
                >
                  Mark as Sent
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Quotes;